(() => {
  if (window.diInteractiveReady) return;
  window.diInteractiveReady = true;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const cleanup = new WeakMap();
  const initTabs = (root) => {
    if (root.dataset.diReady) return;
    const list = root.querySelector('[data-di-tablist]');
    const tabs = Array.from(root.querySelectorAll('[data-di-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-di-panel]'));
    if (!list || !tabs.length || !panels.length || tabs.some(tab => !panels.some(panel => panel.id === tab.dataset.diTab))) return;
    root.dataset.diReady = 'true';
    const abort = new AbortController();
    const signal = abort.signal;
    cleanup.set(root, () => { abort.abort(); delete root.dataset.diReady; });
    list.hidden = false;
    list.setAttribute('role', 'tablist');
    if (root.classList.contains('di-worlds-tabs')) {
      const wide = window.matchMedia('(min-width: 768px)');
      const orient = () => list.setAttribute('aria-orientation', wide.matches ? 'vertical' : 'horizontal');
      orient();
      wide.addEventListener('change', orient, { signal });
    }
    root.classList.add('is-enhanced');
    const activate = (selected, focus) => {
      tabs.forEach((tab) => {
        const active = tab === selected;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', tab.dataset.diTab);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        const panel = panels.find(item => item.id === tab.dataset.diTab);
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        panel.tabIndex = 0;
        panel.hidden = !active;
      });
      if (focus) selected.focus();
    };
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => { root.dataset.diInteracted = 'true'; activate(tab, false); }, { signal });
      tab.addEventListener('keydown', (event) => {
        let next;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        root.dataset.diInteracted = 'true';
        activate(tabs[next], true);
      }, { signal });
    });
    root.addEventListener('shopify:block:select', event => {
      const panel = event.target.closest('[data-di-panel]');
      if (panel) {
        const tab = tabs.find(item => item.dataset.diTab === panel.id);
        if (tab) activate(tab, false);
      }
    }, { signal });
    activate(tabs[0], false);
  };
  const initFilm = (root) => {
    if (root.dataset.diReady) return;
    const video = root.querySelector('video');
    const source = root.querySelector('source[data-src]');
    const button = root.querySelector('[data-di-film-control]');
    const label = root.querySelector('[data-di-film-label]');
    const status = root.querySelector('[data-di-film-status]');
    if (!video || !source || !button || !label) return;
    root.dataset.diReady = 'true';
    const abort = new AbortController();
    const signal = abort.signal;
    let observer;
    let visible = true;
    let disposed = false;
    cleanup.set(root, () => {
      disposed = true;
      abort.abort();
      observer?.disconnect();
      video.pause();
      root.classList.remove('has-film');
      delete root.dataset.diReady;
    });
    button.hidden = false;
    let pending = false;
    const reflect = () => {
      const playing = !video.paused && !video.ended;
      button.setAttribute('aria-pressed', String(playing));
      label.textContent = playing ? button.dataset.pause : button.dataset.play;
    };
    const fail = () => {
      pending = false;
      button.disabled = false;
      root.classList.remove('has-film');
      reflect();
      if (status) status.textContent = button.dataset.error;
    };
    button.addEventListener('click', async () => {
      if (pending) return;
      if (!video.paused) { video.pause(); return; }
      pending = true;
      if (status) status.textContent = '';
      if (!source.src) { source.src = source.dataset.src; video.load(); }
      try {
        await video.play();
        if (disposed || document.hidden || !visible) { video.pause(); pending = false; return; }
        root.classList.add('has-film');
      } catch { if (!disposed) fail(); }
      pending = false;
      if (!disposed) reflect();
    }, { signal });
    video.addEventListener('play', reflect, { signal });
    video.addEventListener('pause', reflect, { signal });
    video.addEventListener('ended', () => { root.classList.remove('has-film'); reflect(); }, { signal });
    video.addEventListener('error', fail, { signal });
    document.addEventListener('visibilitychange', () => { if (document.hidden) video.pause(); }, { signal });
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; if (!visible) video.pause(); }, { threshold: 0.1 });
      observer.observe(root);
    }
  };
  const init = (scope = document) => {
    scope.querySelectorAll('[data-di-tabs]').forEach(initTabs);
    scope.querySelectorAll('[data-di-film]').forEach(initFilm);
  };
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-di-tabs], [data-di-film]').forEach(root => {
      cleanup.get(root)?.();
      cleanup.delete(root);
    });
  });
  let queued = false;
  const progress = () => {
    queued = false;
    const line = document.querySelector('[data-di-progress]');
    if (!line || reducedMotion.matches) return;
    const length = document.documentElement.scrollHeight - window.innerHeight;
    const amount = length > 0 ? Math.min(1, Math.max(0, window.scrollY / length)) : 0;
    line.style.transform = `scaleX(${amount})`;
  };
  window.addEventListener('scroll', () => { if (!queued && !reducedMotion.matches) { queued = true; window.requestAnimationFrame(progress); } }, { passive: true });
  window.addEventListener('resize', progress, { passive: true });
  reducedMotion.addEventListener('change', progress);
  progress();
})();
